import { useQueryClient } from '@tanstack/react-query'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { CheckCircle, ExternalLink, Flame, MinusCircle, Snowflake, User, XCircle } from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { ConfirmationDialog } from 'components/reusable/ConfirmationDialog'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Badge, IconButton, Tooltip } from 'slab'
import { getLocaleDateString } from 'utils'
import {
  getGetBrokersMembersMemberIdCardsQueryKey,
  useGetBrokersMembersMemberIdCards,
  usePostBrokersMembersMemberIdCardsCardIdFreeze,
  usePostBrokersMembersMemberIdCardsCardIdUnfreeze,
} from 'utils/web_api'
import { type BrokersCard, BrokersCardStatus, BrokersCardType } from 'utils/web_api.schemas'

import { CardholderDetailsSheet } from './CardholderDetailsSheet'

type CardsTableProps = {
  memberId: string
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const columnHelper = createColumnHelper<BrokersCard>()

const StatusBadge = ({ status }: { status: BrokersCard['status'] }) => {
  switch (status) {
    case BrokersCardStatus.Active:
      return <Badge type='success' leftIcon={CheckCircle}>Active</Badge>
    case BrokersCardStatus.Inactive:
      return <Badge type='neutral' leftIcon={MinusCircle}>Inactive</Badge>
    case BrokersCardStatus.Frozen:
      return <Badge type='warning' leftIcon={Snowflake}>Frozen</Badge>
    case BrokersCardStatus.ClosedByCustomer:
      return <Badge type='critical' leftIcon={XCircle}>Closed</Badge>
    case BrokersCardStatus.Stolen:
      return <Badge type='critical' leftIcon={XCircle}>Stolen</Badge>
    case BrokersCardStatus.Lost:
      return <Badge type='critical' leftIcon={XCircle}>Lost</Badge>
    default:
      return <Badge type='neutral'>{status}</Badge>
  }
}

const TypeBadge = ({ type }: { type: BrokersCard['type'] }) => {
  switch (type) {
    case BrokersCardType.IndividualVirtualHRACard:
      return <Badge type='info'>Virtual HRA</Badge>
    case BrokersCardType.IndividualPhysicalHRACard:
      return <Badge type='success'>Physical HRA</Badge>
    case BrokersCardType.IndividualVirtualDebitCard:
      return <Badge type='neutral'>Legacy Virtual</Badge>
    case BrokersCardType.IndividualPhysicalDebitCard:
      return <Badge type='warning'>Legacy Physical</Badge>
    default:
      return <Badge type='neutral'>{type}</Badge>
  }
}

const CardsTable = ({
  memberId,
  pagination,
  setPagination,
}: CardsTableProps) => {
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error } =
    useGetBrokersMembersMemberIdCards(memberId, {
      'page[number]': pagination.pageIndex,
      'page[size]': pagination.pageSize,
    }, {
      query: {
        enabled: !!memberId,
        refetchOnWindowFocus: false,
      },
    })

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  const [freezeModalOpen, setFreezeModalOpen] = useState(false)
  const [selectedCard, setSelectedCard] = useState<BrokersCard | null>(null)
  const [cardholderDetailsModalOpen, setCardholderDetailsModalOpen] = useState(false)
  const [selectedCardForDetails, setSelectedCardForDetails] = useState<BrokersCard | null>(null)

  const freezeMutation = usePostBrokersMembersMemberIdCardsCardIdFreeze({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetBrokersMembersMemberIdCardsQueryKey(memberId) })
        toast.success('Card frozen successfully')
        setFreezeModalOpen(false)
        setSelectedCard(null)
      },
      onError: (error) => {
        toast.error((error as GrapeAPIErrorType)?.errors?.[0] ?? 'Failed to freeze card')
      },
    },
  })

  const unfreezeMutation = usePostBrokersMembersMemberIdCardsCardIdUnfreeze({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetBrokersMembersMemberIdCardsQueryKey(memberId) })
        toast.success('Card unfrozen successfully')
      },
      onError: (error) => {
        toast.error((error as GrapeAPIErrorType)?.errors?.[0] ?? 'Failed to unfreeze card')
      },
    },
  })

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: 'Actions',
        cell: ({ row }) => {
          const card = row.original
          return (
            <div className='flex items-center gap-2'>
              {card.remote_link && (
                <DisplayTooltipOnHover text='View card in dashboard'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(card.remote_link, '_blank', 'noopener noreferrer')
                    }}
                  >
                    <ExternalLink size={20} className='stroke-current text-gray-600' />
                  </div>
                </DisplayTooltipOnHover>
              )}
              {card.status === BrokersCardStatus.Active && (
                <Tooltip content='Freeze card'>
                  <IconButton
                    icon={Snowflake}
                    label='Freeze card'
                    onClick={(e) => {
                      e.preventDefault()
                      setSelectedCard(card)
                      setFreezeModalOpen(true)
                    }}
                  />
                </Tooltip>
              )}
              {card.status === BrokersCardStatus.Frozen && (
                <DisplayTooltipOnHover text='Unfreeze card'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.preventDefault()
                      unfreezeMutation.mutate({ memberId, cardId: card.id })
                    }}
                  >
                    <Flame size={20} className='stroke-current text-orange-500' />
                  </div>
                </DisplayTooltipOnHover>
              )}
              <DisplayTooltipOnHover text='Cardholder details'>
                <div
                  className='cursor-pointer h-full'
                  onClick={(e) => {
                    e.preventDefault()
                    setSelectedCardForDetails(card)
                    setCardholderDetailsModalOpen(true)
                  }}
                >
                  <User size={20} className='stroke-current text-gray-600' />
                </div>
              </DisplayTooltipOnHover>
            </div>
          )
        },
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        cell: (info) => <TypeBadge type={info.getValue()} />,
      }),
      columnHelper.accessor('last4_digits', {
        header: 'Last 4',
        cell: (info) => <Badge type='info'>{info.getValue()}</Badge>,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
      columnHelper.accessor('updated_at', {
        header: 'Last Updated',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
    ],
    []
  )

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        errorMessage={(error as GrapeAPIErrorType)?.errors[0]}
        noDataMessage='No cards found for this member.'
        paginationLabelSingular='card'
        paginationMetadata={paginationMetadata}
        tableMessageHeightClass='h-32'
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />

      <ConfirmationDialog
        open={freezeModalOpen}
        onOpenChange={setFreezeModalOpen}
        title='Are you sure?'
        description='Freezing this card will cause any premium payments or QME transactions against it to decline. Please make sure the card is no longer being used before doing this.'
        submitLabel='Freeze card'
        cancelLabel='Back'
        onSubmit={() => {
          if (selectedCard) {
            freezeMutation.mutate({ memberId, cardId: selectedCard.id })
          }
        }}
        isSubmitting={freezeMutation.isPending}
        isDanger
      />

      <CardholderDetailsSheet
        open={cardholderDetailsModalOpen}
        onOpenChange={(open) => {
          setCardholderDetailsModalOpen(open)
          if (!open) {
            setSelectedCardForDetails(null)
          }
        }}
        memberId={memberId}
        card={selectedCardForDetails}
      />
    </>
  )
}

export default CardsTable
